import { useEffect, useState } from "react";
import { supabase } from "../../supabase";

function toKSTDate(date) {
  return new Date(date.getTime() + 9 * 60 * 60 * 1000);
}

function toKSTDateString(date) {
  return toKSTDate(date).toISOString().slice(0, 10);
}

function getMonthRange(year, month) {
  const start = new Date(Date.UTC(year, month, 1) - 9 * 60 * 60 * 1000);
  const end = new Date(Date.UTC(year, month + 1, 1) - 9 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function groupByDayInMonth(dates, year, month) {
  const result = {};
  const days = getDaysInMonth(year, month);
  for (let d = 1; d <= days; d++) {
    const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    result[key] = 0;
  }
  dates.forEach((dt) => {
    if (!dt) return;
    const day = toKSTDateString(new Date(dt));
    if (day in result) result[day]++;
  });
  return result;
}

function MonthNav({ year, month, onChange }) {
  const label = `${year}년 ${month + 1}월`;
  const now = new Date();
  const kstNow = toKSTDate(now);
  const isCurrentMonth = year === kstNow.getUTCFullYear() && month === kstNow.getUTCMonth();

  return (
    <div className="month-nav">
      <button disabled={year <= 2026 && month <= 0} onClick={() => {
        const d = new Date(year, month - 1, 1);
        onChange(d.getFullYear(), d.getMonth());
      }}>◀</button>
      <span className="month-nav-label">{label}</span>
      <button disabled={isCurrentMonth} onClick={() => {
        const d = new Date(year, month + 1, 1);
        onChange(d.getFullYear(), d.getMonth());
      }}>▶</button>
    </div>
  );
}

export default function AdminStats() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const kstNow = toKSTDate(new Date());
  const [signupMonth, setSignupMonth] = useState({ year: kstNow.getUTCFullYear(), month: kstNow.getUTCMonth() });
  const [downloadMonth, setDownloadMonth] = useState({ year: kstNow.getUTCFullYear(), month: kstNow.getUTCMonth() });
  const [pageViewMonth, setPageViewMonth] = useState({ year: kstNow.getUTCFullYear(), month: kstNow.getUTCMonth() });
  const [aiMonth, setAiMonth] = useState({ year: kstNow.getUTCFullYear(), month: kstNow.getUTCMonth() });

  // 차트 데이터
  const [signupByDay, setSignupByDay] = useState({});
  const [downloadByDay, setDownloadByDay] = useState({});
  const [aiByDay, setAiByDay] = useState({});
  /* 위 두 개는 "회원 + 비회원 합계", 아래 두 개는 그중 비회원 몫 (막대 위 설명에 쓴다).
     비회원 활동은 하루 3회 제한을 세려고 만든 별도 표(guest_usage_logs)에 쌓인다 —
     통계가 그 표를 안 읽어서 여태 그래프가 비어 보였다 (2026-09-15 김현비). */
  const [downloadGuestByDay, setDownloadGuestByDay] = useState({});
  const [aiGuestByDay, setAiGuestByDay] = useState({});
  const [pageViewByDay, setPageViewByDay] = useState({});
  const [totalPageViews, setTotalPageViews] = useState(0);

  // 기본 통계 (월과 무관)
  useEffect(() => {
    loadBaseStats();
  }, []);

  // 가입 추이
  useEffect(() => {
    loadSignupChart(signupMonth.year, signupMonth.month);
  }, [signupMonth]);

  // 다운로드 추이
  useEffect(() => {
    loadDownloadChart(downloadMonth.year, downloadMonth.month);
  }, [downloadMonth]);

  // AI 생성 추이
  useEffect(() => {
    loadAiChart(aiMonth.year, aiMonth.month);
  }, [aiMonth]);

  // 페이지뷰 추이
  useEffect(() => {
    loadPageViewChart(pageViewMonth.year, pageViewMonth.month);
  }, [pageViewMonth]);

  async function getAdminIds() {
    const { data } = await supabase.from("profiles").select("id").eq("role", "admin");
    return new Set((data || []).map((p) => p.id));
  }

  async function loadSignupChart(year, month) {
    const { start, end } = getMonthRange(year, month);
    const { data } = await supabase
      .from("profiles")
      .select("created_at, role")
      .gte("created_at", start)
      .lt("created_at", end);
    const filtered = (data || []).filter((r) => r.role !== "admin").map((r) => r.created_at);
    setSignupByDay(groupByDayInMonth(filtered, year, month));
  }

  async function loadDownloadChart(year, month) {
    const { start, end } = getMonthRange(year, month);
    const adminIds = await getAdminIds();
    const [memberRes, guestRes] = await Promise.all([
      supabase
        .from("download_logs")
        .select("created_at, user_id")
        .gte("created_at", start)
        .lt("created_at", end),
      supabase
        .from("guest_usage_logs")
        .select("created_at")
        .eq("action_type", "stl_download")
        .gte("created_at", start)
        .lt("created_at", end),
    ]);
    const member = (memberRes.data || []).filter((r) => !adminIds.has(r.user_id)).map((r) => r.created_at);
    const guest = (guestRes.data || []).map((r) => r.created_at);
    setDownloadByDay(groupByDayInMonth([...member, ...guest], year, month));
    setDownloadGuestByDay(groupByDayInMonth(guest, year, month));
  }

  async function loadAiChart(year, month) {
    const { start, end } = getMonthRange(year, month);
    const adminIds = await getAdminIds();
    const [memberRes, guestRes] = await Promise.all([
      supabase
        .from("ai_design_logs")
        .select("created_at, user_id")
        .gte("created_at", start)
        .lt("created_at", end),
      supabase
        .from("guest_usage_logs")
        .select("created_at")
        .eq("action_type", "ai_design")
        .gte("created_at", start)
        .lt("created_at", end),
    ]);
    const member = (memberRes.data || []).filter((r) => !adminIds.has(r.user_id)).map((r) => r.created_at);
    const guest = (guestRes.data || []).map((r) => r.created_at);
    setAiByDay(groupByDayInMonth([...member, ...guest], year, month));
    setAiGuestByDay(groupByDayInMonth(guest, year, month));
  }

  async function loadPageViewChart(year, month) {
    const { start, end } = getMonthRange(year, month);
    const adminIds = await getAdminIds();
    const { data } = await supabase
      .from("page_views")
      .select("path, duration_sec, created_at, user_id")
      .gte("created_at", start)
      .lt("created_at", end);
    const filtered = (data || []).filter((pv) => !adminIds.has(pv.user_id));
    setPageViewByDay(groupByDayInMonth(filtered.map((r) => r.created_at), year, month));
    setTotalPageViews(filtered.length);

    // 인기 페이지도 업데이트
    const pageStats = {};
    filtered.forEach((pv) => {
      const p = pv.path || "/";
      if (!pageStats[p]) pageStats[p] = { views: 0, totalDuration: 0, durationCount: 0 };
      pageStats[p].views++;
      if (pv.duration_sec && pv.duration_sec > 0) {
        pageStats[p].totalDuration += pv.duration_sec;
        pageStats[p].durationCount++;
      }
    });
    const topPages = Object.entries(pageStats)
      .map(([path, s]) => ({
        path,
        views: s.views,
        avgDuration: s.durationCount > 0 ? Math.round(s.totalDuration / s.durationCount) : 0,
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    setStats((prev) => prev ? { ...prev, topPages } : prev);
  }

  async function loadBaseStats() {
    setLoading(true);

    const adminIds = await getAdminIds();

    const [planRes, filesRes] = await Promise.all([
      supabase.from("profiles").select("id, plan, email, username, role, country"),
      supabase.from("user_files").select("size_mb, user_id").eq("is_deleted", false),
    ]);

    // 플랜별 유저 분포
    const planCount = {};
    (planRes.data || []).filter((r) => r.role !== "admin").forEach((r) => {
      const p = r.plan || "FREE";
      planCount[p] = (planCount[p] || 0) + 1;
    });
    const planOrder = ["FREE", "STANDARD", "PRO"];
    const planDist = {};
    planOrder.forEach((p) => { if (planCount[p]) planDist[p] = planCount[p]; });
    Object.keys(planCount).forEach((p) => { if (!planDist[p]) planDist[p] = planCount[p]; });

    // 국가별
    const countryCount = {};
    (planRes.data || []).filter((r) => r.role !== "admin").forEach((r) => {
      const c = r.country || "Unknown";
      countryCount[c] = (countryCount[c] || 0) + 1;
    });
    const countryDist = Object.entries(countryCount).sort((a, b) => b[1] - a[1]);

    // 스토리지
    const files = (filesRes.data || []).filter((f) => !adminIds.has(f.user_id));
    const totalStorageMb = files.reduce((sum, f) => sum + (f.size_mb || 0), 0);
    const userStorage = {};
    files.forEach((f) => { userStorage[f.user_id] = (userStorage[f.user_id] || 0) + (f.size_mb || 0); });
    const profileMap = {};
    (planRes.data || []).forEach((p) => { profileMap[p.id] = p.username || p.email || p.id.slice(0, 8); });
    const topUsers = Object.entries(userStorage)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([uid, mb]) => ({ name: profileMap[uid] || uid.slice(0, 8), mb }));

    setStats({
      planDist,
      countryDist,
      totalStorageMb: Math.round(totalStorageMb * 100) / 100,
      avgStorageMb: Object.keys(userStorage).length > 0
        ? Math.round((totalStorageMb / Object.keys(userStorage).length) * 100) / 100
        : 0,
      topUsers,
      topPages: [],
    });

    setLoading(false);
  }

  if (loading) return <div>로딩 중…</div>;
  if (!stats) return <div>데이터 없음</div>;

  const maxSignup = Math.max(...Object.values(signupByDay), 1);
  const maxDownload = Math.max(...Object.values(downloadByDay), 1);
  const maxAi = Math.max(...Object.values(aiByDay), 1);
  const maxPageView = Math.max(...Object.values(pageViewByDay), 1);

  return (
    <div className="admin-page">
      <h2>통계</h2>

      {/* 가입 추이 */}
      <div className="admin-section">
        <div className="stat-header">
          <h3>가입 추이</h3>
          <MonthNav year={signupMonth.year} month={signupMonth.month}
            onChange={(y, m) => setSignupMonth({ year: y, month: m })} />
        </div>
        <div className="stat-bar-chart">
          {Object.entries(signupByDay).map(([day, count]) => (
            <div key={day} className="stat-bar-col">
              {count > 0 && <span className="stat-bar-value">{count}</span>}
              <div
                className="stat-bar"
                style={{ height: `${Math.max((count / maxSignup) * 100, count > 0 ? 4 : 2)}px` }}
                title={`${day}: ${count}명`}
              />
              <span className="stat-bar-label">{day.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 다운로드 추이 */}
      <div className="admin-section">
        <div className="stat-header">
          <h3>STL 다운로드 추이</h3>
          <MonthNav year={downloadMonth.year} month={downloadMonth.month}
            onChange={(y, m) => setDownloadMonth({ year: y, month: m })} />
        </div>
        <div className="stat-bar-chart">
          {Object.entries(downloadByDay).map(([day, count]) => (
            <div key={day} className="stat-bar-col">
              {count > 0 && <span className="stat-bar-value">{count}</span>}
              <div
                className="stat-bar download"
                style={{ height: `${Math.max((count / maxDownload) * 100, count > 0 ? 4 : 2)}px` }}
                title={`${day}: ${count}건 (회원 ${count - (downloadGuestByDay[day] || 0)} · 비회원 ${downloadGuestByDay[day] || 0})`}
              />
              <span className="stat-bar-label">{day.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI 생성 추이 */}
      <div className="admin-section">
        <div className="stat-header">
          <h3>AI 디자인 생성 추이</h3>
          <MonthNav year={aiMonth.year} month={aiMonth.month}
            onChange={(y, m) => setAiMonth({ year: y, month: m })} />
        </div>
        <div className="stat-bar-chart">
          {Object.entries(aiByDay).map(([day, count]) => (
            <div key={day} className="stat-bar-col">
              {count > 0 && <span className="stat-bar-value">{count}</span>}
              <div
                className="stat-bar ai"
                style={{ height: `${Math.max((count / maxAi) * 100, count > 0 ? 4 : 2)}px` }}
                title={`${day}: ${count}회 (회원 ${count - (aiGuestByDay[day] || 0)} · 비회원 ${aiGuestByDay[day] || 0})`}
              />
              <span className="stat-bar-label">{day.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 국가별 유저 분포 */}
      <div className="admin-section">
        <h3>국가별 유저 분포</h3>
        <div className="stat-country-list">
          {stats.countryDist.map(([country, count]) => {
            const total = stats.countryDist.reduce((s, [, c]) => s + c, 0);
            const pct = Math.round((count / total) * 100);
            return (
              <div key={country} className="stat-country-row">
                <span className="stat-country-name">{country}</span>
                <div className="stat-country-bar-wrap">
                  <div className="stat-country-bar" style={{ width: `${pct}%` }} />
                </div>
                <span className="stat-country-count">{count}명 ({pct}%)</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 플랜별 유저 분포 */}
      <div className="admin-section">
        <h3>플랜별 유저 분포</h3>
        <div className="stat-plan-inline">
          {Object.entries(stats.planDist).map(([plan, count]) => {
            const total = Object.values(stats.planDist).reduce((s, c) => s + c, 0);
            const pct = Math.round((count / total) * 100);
            return (
              <div key={plan} className="stat-plan-chip">
                <span className={`admin-badge ${plan === "PRO" ? "purple" : plan === "STANDARD" ? "blue" : ""}`}>
                  {plan}
                </span>
                <span className="stat-plan-count">{count}명 ({pct}%)</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 스토리지 현황 */}
      <div className="admin-section">
        <h3>스토리지 현황</h3>
        <div className="stat-storage-summary">
          <div className="stat-storage-chip">
            <span className="stat-storage-label">총 사용량</span>
            <strong>{stats.totalStorageMb} MB</strong>
          </div>
          <div className="stat-storage-chip">
            <span className="stat-storage-label">유저 평균</span>
            <strong>{stats.avgStorageMb} MB</strong>
          </div>
        </div>
        {stats.topUsers.length > 0 && (
          <>
            <h4 style={{ marginTop: 16, fontSize: 13, color: "#666" }}>Top 5 사용자</h4>
            <div className="stat-top-list">
              {stats.topUsers.map((u, i) => (
                <div key={i} className="stat-top-row">
                  <span>{i + 1}. {u.name}</span>
                  <span>{Math.round(u.mb * 100) / 100} MB</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 페이지뷰 추이 */}
      <div className="admin-section">
        <div className="stat-header">
          <h3>
            페이지뷰 추이
            <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 12, color: "#666" }}>
              총 {totalPageViews}회
            </span>
          </h3>
          <MonthNav year={pageViewMonth.year} month={pageViewMonth.month}
            onChange={(y, m) => setPageViewMonth({ year: y, month: m })} />
        </div>
        <div className="stat-bar-chart">
          {Object.entries(pageViewByDay).map(([day, count]) => (
            <div key={day} className="stat-bar-col">
              {count > 0 && <span className="stat-bar-value">{count}</span>}
              <div
                className="stat-bar pageview"
                style={{ height: `${Math.max((count / maxPageView) * 100, count > 0 ? 4 : 2)}px` }}
                title={`${day}: ${count}뷰`}
              />
              <span className="stat-bar-label">{day.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 인기 페이지 */}
      <div className="admin-section">
        <h3>인기 페이지 ({pageViewMonth.year}년 {pageViewMonth.month + 1}월)</h3>
        {stats.topPages.length > 0 ? (
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>페이지</th>
                <th>조회수</th>
                <th>평균 체류시간</th>
              </tr>
            </thead>
            <tbody>
              {stats.topPages.map((pg, i) => (
                <tr key={pg.path}>
                  <td>{i + 1}</td>
                  <td>
                    <a
                      href={pg.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontFamily: "monospace", fontSize: 13, color: "#2563eb", textDecoration: "none" }}
                    >
                      {pg.path}
                    </a>
                  </td>
                  <td>{pg.views}</td>
                  <td>{formatDuration(pg.avgDuration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ color: "#999", padding: 12 }}>페이지뷰 데이터 없음</div>
        )}
      </div>
    </div>
  );
}

function formatDuration(sec) {
  if (!sec || sec <= 0) return "-";
  if (sec < 60) return `${sec}초`;
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${min}분 ${s}초` : `${min}분`;
}
